import { useState, type ReactNode } from 'react';

import type { BusinessReport } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';
import { paymentMethodLabel } from '../lib/paymentMethods';
import { shareOf } from '../lib/reports';

type Report = BusinessReport;

/** How many rows a ranked list shows before "Ver todos". */
const RANKED_PREVIEW = 6;

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function ReportSection({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="section report-section" aria-label={title}>
      <header className="report-section-head">
        <h2 className="section-label">{title}</h2>
        {note ? <p className="row-sub">{note}</p> : null}
      </header>
      {children}
    </section>
  );
}

function MoneyRow({ label, cents, sub, tone }: { label: string; cents: number; sub?: string; tone?: 'minus' | 'total' }) {
  return (
    <li className="report-row" data-tone={tone}>
      <span className="grow">
        <span className="report-row-label">{label}</span>
        {sub ? <span className="row-sub">{sub}</span> : null}
      </span>
      <span className="report-amount">
        {tone === 'minus' && cents > 0 ? `−${formatMoneyCents(cents)}` : formatMoneyCents(cents)}
      </span>
    </li>
  );
}

/** A ranked row: name, what it counts, its amount, and a thin bar for its share of the total. */
function ShareRow({
  label,
  detail,
  cents,
  total,
  swatch,
}: {
  label: string;
  detail: string;
  cents: number;
  total: number;
  swatch?: string;
}) {
  const share = shareOf(cents, total);
  return (
    <li className="report-row report-share-row">
      <span className="grow">
        <span className="report-row-label">
          {swatch ? <span className="report-swatch" style={{ background: swatch }} aria-hidden="true" /> : null}
          {label}
        </span>
        <span className="row-sub">{detail}</span>
      </span>
      <span className="report-share">
        <span className="report-amount">{formatMoneyCents(cents)}</span>
        <span className="row-sub">{share} %</span>
      </span>
      <span className="report-share-bar" aria-hidden="true">
        <span style={{ transform: `scaleX(${total > 0 ? cents / total : 0})`, background: swatch }} />
      </span>
    </li>
  );
}

function RankedList<T>({
  items,
  render,
  empty,
}: {
  items: T[];
  render: (item: T) => ReactNode;
  empty: string;
}) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) return <p className="row-sub report-empty">{empty}</p>;
  const visible = expanded ? items : items.slice(0, RANKED_PREVIEW);
  return (
    <>
      <ul className="group rows report-rows">{visible.map(render)}</ul>
      {items.length > RANKED_PREVIEW ? (
        <button type="button" className="btn small ghost report-more" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Ver menos' : `Ver los ${items.length}`}
        </button>
      ) : null}
    </>
  );
}

export function SalesBreakdown({ report }: { report: Report }) {
  const { sales } = report;
  return (
    <ReportSection title="Ventas" note="De lo vendido a lo que entró.">
      <ul className="group rows report-rows">
        <MoneyRow label="Ventas brutas" cents={sales.grossCents} sub={plural(sales.chargeCount, 'cobro', 'cobros')} />
        <MoneyRow label="Descuentos" cents={sales.discountCents} tone="minus" />
        <MoneyRow label="Ventas netas" cents={sales.netCents} tone="total" />
        <MoneyRow label="Cobrado" cents={sales.collectedCents} />
        {sales.outstandingCents > 0 ? (
          <MoneyRow label="Por cobrar" cents={sales.outstandingCents} sub="Abonos que falta completar" />
        ) : null}
        {sales.bookingRevenueCents > 0 ? (
          <MoneyRow label="Reservas completadas sin cobro" cents={sales.bookingRevenueCents} sub="Cuentan al precio de la reserva" />
        ) : null}
        <MoneyRow label="Ingresos" cents={sales.revenueCents} tone="total" />
        {sales.voidedCount > 0 ? (
          <MoneyRow
            label="Anulados"
            cents={sales.voidedCents}
            sub={`${plural(sales.voidedCount, 'cobro anulado', 'cobros anulados')}, no suman`}
          />
        ) : null}
      </ul>
    </ReportSection>
  );
}

const METHOD_SWATCH: Record<string, string> = {
  efectivo: 'var(--chart-pago-efectivo)',
  tarjeta: 'var(--chart-pago-tarjeta)',
  yappy: 'var(--chart-pago-yappy)',
  transferencia: 'var(--chart-pago-transferencia)',
  banco_aliado: 'var(--chart-pago-banco_aliado)',
  otro: 'var(--chart-pago-otro)',
};

export function PaymentMethodsReport({ report }: { report: Report }) {
  const total = report.paymentMethods.reduce((sum, entry) => sum + entry.amountCents, 0);
  const methods = [...report.paymentMethods].filter((entry) => entry.amountCents > 0).sort((a, b) => b.amountCents - a.amountCents);
  return (
    <ReportSection title="Métodos de pago" note="Para cuadrar la caja.">
      <RankedList
        items={methods}
        empty="Ningún cobro en este período."
        render={(entry) => (
          <ShareRow
            key={entry.method}
            label={paymentMethodLabel(entry.method)}
            detail={plural(entry.count, 'cobro', 'cobros')}
            cents={entry.amountCents}
            total={total}
            swatch={METHOD_SWATCH[entry.method]}
          />
        )}
      />
    </ReportSection>
  );
}

export function TeamReport({ report }: { report: Report }) {
  const total = report.team.reduce((sum, entry) => sum + entry.revenueCents, 0);
  const team = [...report.team].sort((a, b) => b.revenueCents - a.revenueCents);
  return (
    <ReportSection title="Equipo" note="Lo que generó cada persona.">
      <RankedList
        items={team}
        empty="Nadie del equipo tiene visitas en este período."
        render={(entry) => (
          <ShareRow
            key={entry.staffId ?? 'sin-asignar'}
            label={entry.name}
            detail={[
              plural(entry.visits, 'visita', 'visitas'),
              entry.noShows > 0 ? plural(entry.noShows, 'no llegó', 'no llegaron') : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            cents={entry.revenueCents}
            total={total}
          />
        )}
      />
    </ReportSection>
  );
}

export function ServicesReport({ report }: { report: Report }) {
  const total = report.services.reduce((sum, entry) => sum + entry.revenueCents, 0);
  const services = [...report.services].sort((a, b) => b.revenueCents - a.revenueCents);
  return (
    <ReportSection title="Lo que más se vende">
      <RankedList
        items={services}
        empty="Nada vendido en este período."
        render={(entry) => (
          <ShareRow
            key={entry.serviceId ?? entry.name}
            label={entry.name}
            detail={plural(entry.quantity, 'vendido', 'vendidos')}
            cents={entry.revenueCents}
            total={total}
          />
        )}
      />
    </ReportSection>
  );
}

export function CustomersReport({ report }: { report: Report }) {
  const { customers } = report;
  return (
    <ReportSection title="Clientes" note={plural(customers.served, 'cliente atendido', 'clientes atendidos')}>
      <div className="pnl-figures report-figures">
        <div className="pnl-figure">
          <p className="label">Nuevos</p>
          <p className="value">{customers.newCount}</p>
          <p className="row-sub">{formatMoneyCents(customers.newRevenueCents)}</p>
        </div>
        <div className="pnl-figure">
          <p className="label">Recurrentes</p>
          <p className="value">{customers.returningCount}</p>
          <p className="row-sub">{formatMoneyCents(customers.returningRevenueCents)}</p>
        </div>
        <div className="pnl-figure">
          <p className="label">Tasa de regreso</p>
          <p className="value">{customers.served > 0 ? `${shareOf(customers.returningCount, customers.served)} %` : '—'}</p>
          <p className="row-sub">de los atendidos</p>
        </div>
      </div>
      {customers.top.length > 0 ? (
        <>
          <h3 className="report-subhead">Los que más gastaron</h3>
          <ul className="group rows report-rows">
            {customers.top.map((entry) => (
              <MoneyRow
                key={entry.customerId}
                label={entry.name}
                cents={entry.revenueCents}
                sub={plural(entry.visits, 'visita', 'visitas')}
              />
            ))}
          </ul>
        </>
      ) : null}
    </ReportSection>
  );
}

export function BookingsReport({ report }: { report: Report }) {
  const { bookings } = report;
  const attended = bookings.completed + bookings.noShow;
  return (
    <ReportSection title="Reservas">
      <ul className="group rows report-rows">
        <li className="report-row">
          <span className="grow">
            <span className="report-row-label">Completadas</span>
          </span>
          <span className="report-amount">{bookings.completed}</span>
        </li>
        <MoneyRow
          label="No llegaron"
          cents={bookings.noShowValueCents}
          sub={`${plural(bookings.noShow, 'reserva', 'reservas')}${
            attended > 0 ? ` · ${shareOf(bookings.noShow, attended)} % de las citas` : ''
          } · lo que se dejó de cobrar`}
        />
        <MoneyRow
          label="Canceladas"
          cents={bookings.cancelledValueCents}
          sub={`${plural(bookings.cancelled, 'reserva', 'reservas')} · valor de lo cancelado`}
        />
        {bookings.classFillPercent !== null ? (
          <li className="report-row">
            <span className="grow">
              <span className="report-row-label">Ocupación de clases</span>
              <span className="row-sub">Cupos reservados sobre cupos ofrecidos</span>
            </span>
            <span className="report-amount">{Math.round(bookings.classFillPercent)} %</span>
          </li>
        ) : null}
      </ul>
    </ReportSection>
  );
}

export function TaxesReport({ report }: { report: Report }) {
  const { taxes } = report;
  return (
    <ReportSection title="ITBMS y facturas" note="Lo que declara tu contador.">
      {taxes.invoiceCount === 0 && taxes.voidedInvoiceCount === 0 ? (
        <p className="row-sub report-empty">No emitiste facturas en este período.</p>
      ) : (
        <ul className="group rows report-rows">
          <MoneyRow label="Base imponible" cents={taxes.subtotalCents} sub={plural(taxes.invoiceCount, 'factura emitida', 'facturas emitidas')} />
          <MoneyRow label="ITBMS cobrado" cents={taxes.itbmsCents} />
          <MoneyRow label="Total facturado" cents={taxes.totalCents} tone="total" />
          {taxes.voidedInvoiceCount > 0 ? (
            <li className="report-row">
              <span className="grow">
                <span className="report-row-label">Facturas anuladas</span>
                <span className="row-sub">No suman al total</span>
              </span>
              <span className="report-amount">{taxes.voidedInvoiceCount}</span>
            </li>
          ) : null}
        </ul>
      )}
    </ReportSection>
  );
}
